import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  UserPlusIcon, ShieldCheckIcon, CreditCardIcon,
  DocumentCheckIcon, ArrowPathIcon, Cog6ToothIcon, PencilSquareIcon,
  FunnelIcon, ArrowDownTrayIcon, DocumentTextIcon, XMarkIcon,
} from '@heroicons/react/24/outline'
import api from '../../services/api'

interface AuditEvent {
  timestamp: string
  event: string
  description: string
  actor: string
  category: string
}

interface CamsLog {
  timestamp: string
  call_type: string
  pan_masked: string | null
  user_name: string | null
  status: string
  http_status: number | null
  error_message: string | null
  duration_ms: number | null
}

const CATEGORY_META: Record<string, { label: string; icon: React.ComponentType<any>; dot: string }> = {
  onboarding: { label: 'Onboarding', icon: UserPlusIcon,     dot: 'bg-blue-500' },
  kyc:        { label: 'KYC',        icon: ShieldCheckIcon,  dot: 'bg-orange-500' },
  compliance: { label: 'Compliance', icon: DocumentCheckIcon,dot: 'bg-green-500' },
  payment:    { label: 'Payment',    icon: CreditCardIcon,   dot: 'bg-violet-500' },
  admin:      { label: 'Admin',      icon: Cog6ToothIcon,    dot: 'bg-rose-500' },
  content:    { label: 'Content',    icon: PencilSquareIcon, dot: 'bg-cyan-500' },
}

const CAMS_STATUS_META: Record<string, { label: string; cls: string }> = {
  success: { label: 'Success', cls: 'bg-green-500/10 text-green-400 border-green-500/20' },
  failed:  { label: 'Failed',  cls: 'bg-red-500/10 text-red-400 border-red-500/20' },
  blocked: { label: 'Blocked', cls: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
}

const CALL_TYPE_LABEL: Record<string, string> = {
  enquiry: 'PAN Enquiry',
  download: 'PAN Download',
}

function timeAgo(iso: string) {
  const d = new Date(iso)
  const diff = (Date.now() - d.getTime()) / 1000
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-12 h-12 rounded-xl bg-dark-700 flex items-center justify-center mb-4">
        <DocumentTextIcon className="w-6 h-6 text-gray-500" />
      </div>
      <p className="text-white font-semibold">{label}</p>
    </div>
  )
}

// ─── Filters dropdown ──────────────────────────────────────────────────────

interface FilterState {
  category?: string
  dateFrom?: string
  dateTo?: string
}

function FiltersPanel({
  categories, value, onChange, onClose,
}: {
  categories: { key: string; label: string }[]
  value: FilterState
  onChange: (v: FilterState) => void
  onClose: () => void
}) {
  const [draft, setDraft] = useState<FilterState>(value)

  return (
    <div className="absolute right-0 top-full mt-2 w-80 bg-dark-800 border border-dark-600 rounded-xl shadow-xl z-20 p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-white">Filters</p>
        <button onClick={onClose} className="text-gray-400 hover:text-white">
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-xs text-gray-400 mb-1.5 block">Category</label>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={() => setDraft(d => ({ ...d, category: undefined }))}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${!draft.category ? 'bg-primary-500 text-white border-primary-500' : 'bg-dark-700 text-gray-400 border-dark-600 hover:border-dark-500'}`}
            >
              All
            </button>
            {categories.map(c => (
              <button
                key={c.key}
                onClick={() => setDraft(d => ({ ...d, category: c.key }))}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${draft.category === c.key ? 'bg-primary-500 text-white border-primary-500' : 'bg-dark-700 text-gray-400 border-dark-600 hover:border-dark-500'}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-gray-400 mb-1.5 block">From</label>
            <input
              type="date"
              value={draft.dateFrom || ''}
              onChange={e => setDraft(d => ({ ...d, dateFrom: e.target.value || undefined }))}
              className="w-full text-xs bg-dark-700 border border-dark-600 rounded-lg px-2 py-1.5 text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 mb-1.5 block">To</label>
            <input
              type="date"
              value={draft.dateTo || ''}
              onChange={e => setDraft(d => ({ ...d, dateTo: e.target.value || undefined }))}
              className="w-full text-xs bg-dark-700 border border-dark-600 rounded-lg px-2 py-1.5 text-gray-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
        </div>
      </div>

      <div className="flex gap-2 mt-4">
        <button
          onClick={() => { setDraft({}); onChange({}); onClose() }}
          className="flex-1 text-xs px-3 py-2 rounded-lg border border-dark-600 text-gray-300 hover:bg-dark-700 transition-colors"
        >
          Clear
        </button>
        <button
          onClick={() => { onChange(draft); onClose() }}
          className="flex-1 text-xs px-3 py-2 rounded-lg bg-primary-500 text-white hover:bg-primary-600 transition-colors font-medium"
        >
          Apply
        </button>
      </div>
    </div>
  )
}

// ─── Application tab ───────────────────────────────────────────────────────

function ApplicationTab() {
  const [events, setEvents] = useState<AuditEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState<FilterState>({})
  const [showFilters, setShowFilters] = useState(false)
  const [exporting, setExporting] = useState(false)

  const fetchEvents = async () => {
    setLoading(true)
    try {
      const params: Record<string, string> = {}
      if (filters.category) params.category = filters.category
      if (filters.dateFrom) params.date_from = filters.dateFrom
      if (filters.dateTo) params.date_to = filters.dateTo
      const res = await api.get('/admin/audit-trail', { params })
      setEvents(res.data)
    } catch {}
    finally { setLoading(false) }
  }

  useEffect(() => { fetchEvents() }, [filters])

  const handleExport = async () => {
    setExporting(true)
    try {
      const params: Record<string, string> = {}
      if (filters.category) params.category = filters.category
      if (filters.dateFrom) params.date_from = filters.dateFrom
      if (filters.dateTo) params.date_to = filters.dateTo
      const res = await api.get('/admin/audit-trail/export', { params, responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'audit-logs.csv')
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch {}
    finally { setExporting(false) }
  }

  const activeFilterCount = (filters.category ? 1 : 0) + (filters.dateFrom ? 1 : 0) + (filters.dateTo ? 1 : 0)

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <nav className="text-xs text-gray-400 mb-1"><Link to="/admin" className="hover:text-amber-400 transition-colors">RA Desk</Link> &rsaquo; Audit Logs</nav>
          <h1 className="text-2xl font-bold text-white">Audit Logs</h1>
          <p className="text-sm text-gray-400 mt-0.5">Tamper-evident log of all client and compliance events</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <button onClick={() => setShowFilters(s => !s)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-dark-600 bg-dark-800 text-gray-300 text-sm hover:bg-dark-700 transition-colors relative">
              <FunnelIcon style={{ width: 15, height: 15 }} />
              Filters
              {activeFilterCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-primary-500 text-white text-[10px] flex items-center justify-center font-bold">
                  {activeFilterCount}
                </span>
              )}
            </button>
            {showFilters && (
              <FiltersPanel
                categories={Object.entries(CATEGORY_META).map(([key, m]) => ({ key, label: m.label }))}
                value={filters}
                onChange={setFilters}
                onClose={() => setShowFilters(false)}
              />
            )}
          </div>
          <button onClick={handleExport} disabled={exporting}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-primary-600/40 bg-primary-500/10 text-primary-300 text-sm hover:bg-primary-500/20 transition-colors disabled:opacity-50">
            <ArrowDownTrayIcon style={{ width: 15, height: 15 }} />
            Export CSV
          </button>
          <button onClick={fetchEvents}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-dark-600 bg-dark-800 text-gray-300 text-sm hover:bg-dark-700 transition-colors">
            <ArrowPathIcon style={{ width: 15, height: 15 }} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-dark-800 rounded-2xl border border-dark-700 shadow-sm overflow-hidden mt-6">
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading…</div>
        ) : events.length === 0 ? (
          <EmptyState label="No audit logs found" />
        ) : (
          <div className="divide-y divide-dark-700">
            {events.map((ev, i) => {
              const meta = CATEGORY_META[ev.category] || { label: ev.category, icon: DocumentCheckIcon, dot: 'bg-gray-400' }
              const Icon = meta.icon
              return (
                <div key={i} className="flex items-start gap-4 px-6 py-4 hover:bg-dark-700 transition-colors">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-8 h-8 rounded-full bg-dark-700 flex items-center justify-center">
                      <Icon className="text-gray-400" style={{ width: 15, height: 15 }} />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-100">{ev.description}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className={`w-1.5 h-1.5 rounded-full inline-block ${meta.dot}`} />
                      <span className="text-xs text-gray-400">{meta.label}</span>
                      <span className="text-xs text-gray-400">&middot;</span>
                      <span className="text-xs text-gray-400">{ev.actor}</span>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap">{timeAgo(ev.timestamp)}</span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </>
  )
}

// ─── CAMS Audit tab ────────────────────────────────────────────────────────

function CamsAuditTab() {
  const [logs, setLogs] = useState<CamsLog[]>([])
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [exporting, setExporting] = useState(false)

  const fetchLogs = async () => {
    setLoading(true)
    try {
      const params: Record<string, string> = {}
      if (statusFilter) params.status = statusFilter
      const res = await api.get('/admin/cams-audit', { params })
      setLogs(res.data.logs)
    } catch {}
    finally { setLoading(false) }
  }

  useEffect(() => { fetchLogs() }, [statusFilter])

  const handleExport = async () => {
    setExporting(true)
    try {
      const params: Record<string, string> = {}
      if (statusFilter) params.status = statusFilter
      const res = await api.get('/admin/cams-audit/export', { params, responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'cams-audit.csv')
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch {}
    finally { setExporting(false) }
  }

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <nav className="text-xs text-gray-400 mb-1"><Link to="/admin" className="hover:text-amber-400 transition-colors">RA Desk</Link> &rsaquo; CAMS Audit</nav>
          <h1 className="text-2xl font-bold text-white">CAMS API Audit</h1>
          <p className="text-sm text-gray-400 mt-0.5">Every call made to the CAMS KRA external API — enquiry &amp; download</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleExport} disabled={exporting}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-primary-600/40 bg-primary-500/10 text-primary-300 text-sm hover:bg-primary-500/20 transition-colors disabled:opacity-50">
            <ArrowDownTrayIcon style={{ width: 15, height: 15 }} />
            Export CSV
          </button>
          <button onClick={fetchLogs}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-dark-600 bg-dark-800 text-gray-300 text-sm hover:bg-dark-700 transition-colors">
            <ArrowPathIcon style={{ width: 15, height: 15 }} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <button onClick={() => setStatusFilter('')}
          className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${!statusFilter ? 'bg-primary-500 text-white border-primary-500' : 'bg-dark-800 text-gray-400 border-dark-600 hover:border-dark-500'}`}>
          All Calls
        </button>
        {Object.entries(CAMS_STATUS_META).map(([key, meta]) => (
          <button key={key} onClick={() => setStatusFilter(key)}
            className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${statusFilter === key ? 'bg-primary-500 text-white border-primary-500' : 'bg-dark-800 text-gray-400 border-dark-600 hover:border-dark-500'}`}>
            {meta.label}
          </button>
        ))}
      </div>

      <div className="bg-dark-800 rounded-2xl border border-dark-700 shadow-sm overflow-hidden mt-4">
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading…</div>
        ) : logs.length === 0 ? (
          <EmptyState label="No CAMS API calls found" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-dark-700 text-left text-xs text-gray-400 uppercase tracking-wider">
                  <th className="px-6 py-3 font-medium">Timestamp</th>
                  <th className="px-4 py-3 font-medium">Call Type</th>
                  <th className="px-4 py-3 font-medium">PAN</th>
                  <th className="px-4 py-3 font-medium">User</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">HTTP</th>
                  <th className="px-4 py-3 font-medium">Duration</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-700">
                {logs.map((log, i) => {
                  const statusMeta = CAMS_STATUS_META[log.status] || { label: log.status, cls: 'bg-gray-500/10 text-gray-400 border-gray-500/20' }
                  return (
                    <tr key={i} className="hover:bg-dark-700 transition-colors">
                      <td className="px-6 py-3 text-gray-300 whitespace-nowrap">{timeAgo(log.timestamp)}</td>
                      <td className="px-4 py-3 text-gray-100 font-medium">{CALL_TYPE_LABEL[log.call_type] || log.call_type}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-400">{log.pan_masked || '—'}</td>
                      <td className="px-4 py-3 text-gray-300">{log.user_name || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${statusMeta.cls}`}>
                          {statusMeta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-400">{log.http_status || '—'}</td>
                      <td className="px-4 py-3 text-gray-400">{log.duration_ms != null ? `${log.duration_ms}ms` : '—'}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}

// ─── Page ──────────────────────────────────────────────────────────────────

export default function AuditTrailPage() {
  const [tab, setTab] = useState<'application' | 'cams'>('application')

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        <button
          onClick={() => setTab('application')}
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === 'application' ? 'bg-primary-500 text-white' : 'bg-dark-800 text-gray-400 border border-dark-600 hover:border-dark-500'}`}
        >
          Application
        </button>
        <button
          onClick={() => setTab('cams')}
          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${tab === 'cams' ? 'bg-primary-500 text-white' : 'bg-dark-800 text-gray-400 border border-dark-600 hover:border-dark-500'}`}
        >
          CAMS Audit
        </button>
      </div>

      {tab === 'application' ? <ApplicationTab /> : <CamsAuditTab />}
    </div>
  )
}
